import { useEffect, useRef, useState } from "react";

/**
 * Animated number counter that runs once when scrolled into view.
 * - Parses value: extracts numeric portion + preserves prefix/suffix (e.g. "120,000+", "+27 PTS", "99.4%", "12 SEC")
 * - Easing: easeOutCubic (fast → slow)
 * - Respects prefers-reduced-motion
 */
export default function AnimatedCounter({ value, duration = 1600, decimals, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const startedRef = useRef(false);

  // Parse "120,000+" → { prefix: "", num: 120000, suffix: "+" }
  const { prefix, num, suffix, formatTarget, hasDecimal } = parseValue(value);

  useEffect(() => {
    const el = ref.current;
    if (!el || num === null) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setDisplay(value);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          animate(num, duration, hasDecimal, decimals, formatTarget, prefix, suffix, setDisplay);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [num, duration, value, hasDecimal, decimals, formatTarget, prefix, suffix]);

  return (
    <span ref={ref} className={className} data-testid="animated-counter">
      {display}
    </span>
  );
}

function parseValue(value) {
  if (typeof value !== "string") return { prefix: "", num: null, suffix: "", formatTarget: "", hasDecimal: false };
  // Match leading non-digit chars (prefix), then number (allowing , and .), then trailing chars (suffix)
  const m = value.match(/^([^\d.-]*)([-\d.,]+)(.*)$/);
  if (!m) return { prefix: "", num: null, suffix: "", formatTarget: value, hasDecimal: false };
  const prefix = m[1];
  const raw = m[2];
  const suffix = m[3];
  const hasComma = raw.includes(",");
  const hasDecimal = raw.includes(".");
  const num = parseFloat(raw.replace(/,/g, ""));
  if (Number.isNaN(num)) return { prefix: "", num: null, suffix: "", formatTarget: value, hasDecimal: false };
  return {
    prefix,
    num,
    suffix,
    formatTarget: hasComma ? "comma" : "plain",
    hasDecimal,
  };
}

function formatNum(n, formatTarget, hasDecimal, decimals) {
  const d = decimals != null ? decimals : hasDecimal ? 1 : 0;
  if (formatTarget === "comma") {
    return n.toLocaleString("en-US", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  }
  return n.toFixed(d);
}

function animate(target, duration, hasDecimal, decimals, formatTarget, prefix, suffix, setDisplay) {
  const start = performance.now();
  const startVal = 0;
  const tick = (now) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
    const current = startVal + (target - startVal) * eased;
    setDisplay(`${prefix}${formatNum(current, formatTarget, hasDecimal, decimals)}${suffix}`);
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
